import {useQueryClient} from 'react-query';
import React, {useEffect, useMemo, useState} from 'react';
import {useForm, UseFormReturn} from 'react-hook-form';
import {joiResolver} from '@hookform/resolvers/joi';
import {
  PutRegistrationParticularsRequest,
  usePutRegistrationParticulars,
} from '../../../api-refactored/candidate/usePutRegistrationParticulars';
import {
  CandidateProfileFormData,
  CandidateProfileFormDefaultValues,
  CandidateProfileIsDisableInputs,
} from '../../../models';
import { ExamCandidateDetails } from '../../../api/candidate/exam/model';
import { IExamMasterCodeItem } from '../../../api/user/model';
import { getPreferredAddress, getPreferredContactType } from '../../../utils/ComponentUtils';
import candidateProfileSchema from '../../profile/schema/createCandidate';
import { useGetIExamMasterCode } from '../../../api-refactored/user/useGetIExamMasterCode';

const disabledInput: CandidateProfileIsDisableInputs = {
  name: true,
  IdType: true,
  NricFin: true,
  gender: true,
  dateOfBirth: true,
  race: true,
  secondaryRace: true,
  nationality: true,
  prStatus: true,
  nationalityIssueDate: true,
  citizenship: true,
  occupation: true,
  school: true,
};

interface UpdateRegistrationParticularReturn {
  /**
   * React hook form instance
   */
  form: UseFormReturn<Record<string, any>>;
  /**
   * Indicator whether the form is in editing state.
   */
  isEditState: boolean;
  /**
   * Indicator whether it is calling update particular API.
   */
  isLoading: boolean;
  /**
   * Callback method when edit button clicked.
   */
  editHandler: () => void;
  /**
   * Callback method when cancel button clicked.
   */
  cancelHandler: () => void;
  /**
   * Callback method when submit/save button clicked.
   */
  onSubmitHandler: (formData: CandidateProfileFormData) => void;
  /**
   * Form default value.
   */
  formDefaultValues: CandidateProfileFormDefaultValues | undefined;
  /**
   * Indicator if populating default value into form hook is still in progress.
   */
  isSettingUpFormValue: boolean;
}

const useUpdateRegistrationParticulars = ({
  particularDetails,
  examUuid,
  queryKeyToInvalidateAfterUpdate,
  setErrorMsg,
  setReviewParticularErrorMessage,
}: {
  particularDetails: ExamCandidateDetails | undefined;
  examUuid: string;
  queryKeyToInvalidateAfterUpdate: string;
  setErrorMsg: React.Dispatch<React.SetStateAction<string>>;
  setReviewParticularErrorMessage?: React.Dispatch<React.SetStateAction<string>> | undefined;
}): UpdateRegistrationParticularReturn => {
  const queryClient = useQueryClient();
  const [isEditState, setIsEditState] = useState<boolean>(false);
  const { data: iExamsData } = useGetIExamMasterCode();
  const [isSettingUpFormValue, setIsSettingUpFormValue] = useState(true);

  const formDefaultValues: CandidateProfileFormDefaultValues | undefined = useMemo(() => {
    if (!particularDetails || !iExamsData) {
      return undefined;
    }
    return {
      name: particularDetails.name,
      email: particularDetails.email,
      NricFin: particularDetails.nricFinPassport,
      gender: iExamsData?.gender.find(
        ({ codeValueDescription }) =>
          codeValueDescription === particularDetails.gender?.toUpperCase()
      ) as IExamMasterCodeItem,
      race: iExamsData?.race.find(
        ({ codeValueDescription }) => codeValueDescription === particularDetails.race?.toUpperCase()
      ) as IExamMasterCodeItem,
      nationality: iExamsData?.nationality.find(
        ({ codeValueDescription }) => codeValueDescription === particularDetails.nationality
      ) as IExamMasterCodeItem,

      // addresses
      houseOrBlkNo: particularDetails.mailingAddress?.houseOrBlockNumber,
      streetName: particularDetails.mailingAddress?.streetName,
      building: particularDetails.mailingAddress?.buildingName,
      floor: particularDetails.mailingAddress?.floorNumber,
      unit: particularDetails.mailingAddress?.unitNumber,
      postalCode: particularDetails.mailingAddress?.postalCode,
      foreignAddress1: particularDetails.foreignAddress1,
      foreignAddress2: particularDetails.foreignAddress2,
      foreignAddress3: particularDetails.foreignAddress3,
      preferredAddress: getPreferredAddress(particularDetails.localAddressPreferred),

      // contact numbers
      telephoneCountryCode: particularDetails.phoneNumber
          ?.countryCode as IExamMasterCodeItem,
      telephoneContactNumber: particularDetails.phoneNumber?.number,
      mobileCountryCode: particularDetails.mobileNumber
          ?.countryCode as IExamMasterCodeItem,
      mobileContactNumber: particularDetails.mobileNumber?.number,
      preferredTelephoneOrMobile: getPreferredContactType(particularDetails.mobileNumberPreferred),

      // next of kin
      nextOfKinName: particularDetails.nextOfKinName,
      nextOfKinCountryCode: particularDetails.nextOfKin
          ?.countryCode as IExamMasterCodeItem,
      nextOfKinContactNumber: particularDetails.nextOfKin?.number,
      nextOfKinRelationshipToCandidate: {
        codeValueDescription: particularDetails.nextOfKinRelationship,
      } as IExamMasterCodeItem,
    };
  }, [particularDetails, iExamsData]);

  const form = useForm<CandidateProfileFormData>({
    mode: 'onSubmit',
    resolver: joiResolver(
      candidateProfileSchema(disabledInput, 'Next-of-kin Name', 'Next-of-kin Contact No.')
    ),
    defaultValues: formDefaultValues,
  });

  useEffect(
    () => {
      if (formDefaultValues) {
        form.reset(formDefaultValues);
        setIsSettingUpFormValue(false);
      }
    },
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [formDefaultValues]
  );

  const editHandler = () => {
    setErrorMsg('');
    if (setReviewParticularErrorMessage) {
      setReviewParticularErrorMessage('');
    }
    setIsEditState(true);
  };

  const cancelHandler = () => {
    setErrorMsg('');
    if (setReviewParticularErrorMessage) {
      setReviewParticularErrorMessage('');
    }
    setIsEditState(false);
    form.reset();
  };

  const onSubmitHandler = (formData: CandidateProfileFormData) => {
    const data: PutRegistrationParticularsRequest = {
      email: formData.email,
      mailingAddress: {
        houseOrBlockNumber: formData.houseOrBlkNo,
        floorNumber: formData.floor,
        unitNumber: formData.unit,
        buildingName: formData.building,
        streetName: formData.streetName,
        postalCode: formData.postalCode,
      },
      foreignAddress1: formData.foreignAddress1,
      foreignAddress2: formData.foreignAddress2,
      foreignAddress3: formData.foreignAddress3,
      localAddressPreferred: formData.preferredAddress === 'L',
      mobileNumber: {
        countryCode: formData.mobileCountryCode,
        number: formData.mobileContactNumber,
      },
      mobileNumberPreferred: formData.preferredTelephoneOrMobile === 'M',

      nextOfKinName: formData.nextOfKinName,
      nextOfKinContactNumber: {
        countryCode: formData.nextOfKinCountryCode,
        number: formData.nextOfKinContactNumber,
      },
      nextOfKinRelationship: formData.nextOfKinRelationshipToCandidate?.codeValueDescription,
    };
    if (formData.telephoneContactNumber && formData.telephoneCountryCode) {
      data.telephoneNumber = {
        countryCode: formData.telephoneCountryCode,
        number: formData.telephoneContactNumber,
      };
    } else {
      data.telephoneNumber = {
        countryCode: null,
        number: '',
      };
    }
    registrationParticularMutation.mutate(data);
  };

  const registrationParticularMutation = usePutRegistrationParticulars({
    uuid: examUuid,
    onSuccess: (res) => {
      setIsEditState(false);
      queryClient.invalidateQueries(queryKeyToInvalidateAfterUpdate);
    },
    onError: (err) => setErrorMsg(err?.response?.data.message!),
  });

  return {
    form,
    isEditState,
    isLoading: registrationParticularMutation.isLoading,
    editHandler,
    cancelHandler,
    onSubmitHandler,
    formDefaultValues,
    isSettingUpFormValue,
  };
};

export { useUpdateRegistrationParticulars, disabledInput };
